import { builderUpdatePhase, isStartOrEnd, titleCase, type BuilderPlan, type BuilderTree } from '@nsb/core';
import type { ReactNode } from 'react';
import { onBoard } from '../tree/master.ts';
import { NumberInput } from '../ui.tsx';
import { CommitText } from './fields.tsx';
import { weeks } from './weeks.ts';

/**
 * A phase opened from its heading on the master tree (Jason, 2026-10-07: "Phase facts are edited on a card opened from
 * the phase heading on the master ... as a step's card is opened"): its name, what happens in it, the gate that closes
 * it, how sure the price is once it closes and its share of the time (the plan of 2026-10-07, items 1, 2 and 18). The
 * phase's heading is its transition: the gate that closes it docks on the next phase's heading and opens it. Each change
 * goes through the builder's phase patch (core `builderUpdatePhase`); adding, moving and removing phases, and the
 * price's spread at the start, stay in the Phases list under the board.
 */

const q = (t: string) => `“${t}”`;
const small = 'link text-[0.8125rem] text-ink-2 hover:text-ink';

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4">
      <h3 className="label mb-1.5">{title}</h3>
      {children}
    </section>
  );
}

export function PhaseCard({ tree, phase, plan, edit, leave }: { tree: BuilderTree; phase: string; plan: BuilderPlan; edit: (next: BuilderTree, words: string) => void; leave: () => void }) {
  const i = tree.phases.findIndex((p) => p.key === phase);
  const p = tree.phases[i];
  if (!p) return null;
  const gates = tree.nodes.filter((n) => n.kind === 'gate');
  const titleOf = (k: string) => titleCase(tree.nodes.find((n) => n.key === k)?.title ?? k);
  const planned = plan.phases.find((x) => x.key === p.key);
  // Counted as the board counts a phase's steps: those on it, at the top of the tree, not the start or the end.
  const inIt = onBoard(tree).filter((n) => n.phase === p.key && !isStartOrEnd(n)).length;
  const start = Math.round(tree.startSpread * 100);
  const last = i === tree.phases.length - 1;
  const set = (patch: Parameters<typeof builderUpdatePhase>[2], words: string) => edit(builderUpdatePhase(tree, p.key, patch), words);

  return (
    <div className="relative w-full bg-sheet">
      <div className="px-4 pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="label">
            Phase <span className="num">{String(i + 1).padStart(2, '0')}</span> · opened
          </span>
          <button type="button" className={small} onClick={leave}>
            Back to the whole tree (Esc)
          </button>
        </div>
        <CommitText value={p.name} required maxLength={80} aria-label="Name" className="!font-display !text-[1.5rem] !leading-tight" onCommit={(t) => set({ name: t }, `Renamed the phase ${q(p.name)} to ${q(t)}.`)} />
        <p className="meta mt-1.5">
          <span className="num">{inIt}</span> {inIt === 1 ? 'step' : 'steps'} · {planned && planned.end > planned.start ? `${weeks(planned.end - planned.start)} as the board stands` : 'nothing of it on the board yet'}
        </p>
      </div>

      <div className="grid gap-x-6 px-4 pb-4 lg:grid-cols-2">
        <div>
          <Part title="What happens in it">
            <CommitText multiline rows={3} value={p.what} maxLength={500} aria-label="What happens in it" onCommit={(t) => set({ what: t }, `What happens in ${q(p.name)} changed.`)} />
          </Part>
          <Part title="The gate that closes it">
            <select
              className="field"
              aria-label="The gate that closes it"
              value={p.closedBy ?? ''}
              onChange={(e) => {
                const k = e.target.value || null;
                set({ closedBy: k }, k ? `${q(p.name)} is closed by ${q(titleOf(k))} now.` : `${q(p.name)} ends when its last step does now.`);
              }}
            >
              <option value="">Nothing: when its last step ends</option>
              {p.closedBy && !gates.some((n) => n.key === p.closedBy) ? <option value={p.closedBy}>{titleOf(p.closedBy)}</option> : null}
              {gates.map((n) => (
                <option key={n.key} value={n.key}>
                  {titleCase(n.title)}
                </option>
              ))}
            </select>
            {p.closedBy && planned && !planned.closedBy ? (
              <p className="meta mt-1.5">Not on the board: the phase ends with its last step meanwhile.</p>
            ) : !p.closedBy && !last ? (
              <p className="meta mt-1.5">The next phase opens with this phase’s gate: saving for projects needs one.</p>
            ) : p.closedBy && !last ? (
              <p className="meta mt-1.5">It opens the next phase: drawn on that phase’s heading.</p>
            ) : null}
          </Part>
        </div>
        <div>
          <Part title="How sure the price is once it closes">
            <span className="flex flex-wrap items-center gap-2">
              <NumberInput
                className="!w-20"
                value={Math.round(p.sureAfter * 100)}
                aria-label={`Of the first spread, the share still open once ${p.name} closes, per cent`}
                onChange={(n) => {
                  const v = Math.max(0, Math.min(100, Math.round(n ?? 0)));
                  set({ sureAfter: v / 100 }, `${q(p.name)} leaves ${v}% of the first spread open now.`);
                }}
              />
              <span className="meta">
                % of the first spread still open: ±<span className="num">{Math.round(start * p.sureAfter)}</span>% once it closes
              </span>
            </span>
          </Part>
          <Part title="Its share of the time">
            <span className="flex flex-wrap items-center gap-2">
              <NumberInput
                className="!w-20"
                value={typeof p.share === 'number' ? p.share : null}
                aria-label={`${p.name}’s share of the time, per cent`}
                onChange={(n) => {
                  if (n === null) {
                    set({ share: undefined }, `${q(p.name)}’s share of the time is blank again.`);
                    return;
                  }
                  const v = Math.max(0, Math.min(100, Math.round(n)));
                  set({ share: v }, `${q(p.name)} takes ${v}% of the time now.`);
                }}
              />
              <span className="meta">%</span>
            </span>
            <p className="meta mt-1.5">Weighed against the phases still ahead of it: a phase’s gate nobody has dated is placed on a job’s funnel by these shares.</p>
          </Part>
        </div>
      </div>
    </div>
  );
}
